import rawAiFindingsConfig from './aiFindings.json';
import type { TAiFinding, TAiFindingReference, TAiFindingsConfig } from './types';

export const AI_FINDINGS_CONFIG = rawAiFindingsConfig as TAiFindingsConfig;

const normalizeText = (value?: string | null) => value?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';

const getAnchorValue = (element?: Element | null) =>
  element?.getAttribute('id') ?? element?.getAttribute('data-id') ?? undefined;

const isHeadingElement = (element: HTMLElement) => element.matches('h1, h2, h3, h4, h5, h6, .heading-text.h5-ref');

const getHeadingLevel = (element: HTMLElement) => {
  if (/^H[1-6]$/.test(element.tagName)) return Number(element.tagName.slice(1));
  if (element.matches('.heading-text.h5-ref')) return 5;
  return null;
};

const getHeadingLabel = (element: HTMLElement) => {
  if (element.matches('.heading-text.h5-ref')) return element.textContent ?? '';
  return element.querySelector('.heading-text')?.textContent ?? element.textContent ?? '';
};

const findSectionAnchor = (root: HTMLElement, sectionTitle: string) => {
  const headingCandidates = Array.from(
    root.querySelectorAll('h1, h2, h3, h4, h5, h6, .heading-text.h5-ref')
  ) as HTMLElement[];

  const heading = headingCandidates.find((candidate) => {
    const headingLabel = normalizeText(getHeadingLabel(candidate));
    return headingLabel === sectionTitle || headingLabel.includes(sectionTitle);
  });

  if (heading) return heading;

  // Imported spreadsheets do not have heading nodes. Their row and section labels live in
  // anchored cells/paragraphs instead, so use the narrowest exact text match as the anchor.
  const exactTextMatches = Array.from(root.querySelectorAll('[id], [data-id]')).filter(
    (candidate) => Boolean(getAnchorValue(candidate)) && normalizeText(candidate.textContent) === sectionTitle
  ) as HTMLElement[];

  return exactTextMatches.at(-1);
};

export const getAiFindingsConfig = (documentName?: string) => {
  const normalizedDocumentName = normalizeText(documentName);
  if (!normalizedDocumentName) return undefined;

  return AI_FINDINGS_CONFIG.find(
    ({ triggerDocumentName }) => normalizeText(triggerDocumentName) === normalizedDocumentName
  );
};

export const getAiFindings = (documentName?: string) => getAiFindingsConfig(documentName)?.findings ?? [];

export const getAiFindingSummary = (finding: Pick<TAiFinding, 'error' | 'summary'>) =>
  finding.error ?? finding.summary ?? '';

export const doesDocumentMatchAiFindings = (documentName?: string) => Boolean(getAiFindingsConfig(documentName));

export const isAiFindingsVisible = (isAiEnabled: boolean | undefined, documentName?: string) =>
  isAiEnabled === true && doesDocumentMatchAiFindings(documentName);

export const resolveAiFindingScrollValue = (
  root: HTMLElement,
  finding: Pick<TAiFinding | TAiFindingReference, 'scrollTarget' | 'highlightText' | 'highlightTexts'>
) => {
  const sectionTitle = normalizeText(finding.scrollTarget?.section);
  if (!sectionTitle) return undefined;

  const sectionAnchor = findSectionAnchor(root, sectionTitle);
  if (!sectionAnchor) return undefined;

  const primaryHighlightText = finding.highlightTexts?.find(Boolean) ?? finding.highlightText;

  if (!primaryHighlightText) return getAnchorValue(sectionAnchor);

  const normalizedHighlight = normalizeText(primaryHighlightText);
  if (!normalizedHighlight) return getAnchorValue(sectionAnchor);

  const anchorElements = Array.from(root.querySelectorAll('[id], [data-id]')).filter((candidate) =>
    Boolean(getAnchorValue(candidate))
  ) as HTMLElement[];

  const sectionAnchorValue = getAnchorValue(sectionAnchor);
  const sectionAnchorIndex = anchorElements.findIndex((candidate) => getAnchorValue(candidate) === sectionAnchorValue);
  const sectionHeadingLevel = getHeadingLevel(sectionAnchor);

  if (sectionAnchorIndex === -1) return undefined;

  for (let index = sectionAnchorIndex; index < anchorElements.length; index += 1) {
    const candidate = anchorElements[index];

    if (index > sectionAnchorIndex && isHeadingElement(candidate)) {
      const candidateLevel = getHeadingLevel(candidate);
      if (sectionHeadingLevel !== null && candidateLevel !== null && candidateLevel <= sectionHeadingLevel) break;
    }

    if (normalizeText(candidate.textContent).includes(normalizedHighlight)) return getAnchorValue(candidate);
  }

  return undefined;
};
